/* eslint-disable ts/no-unsafe-call */
/* eslint-disable ts/no-unsafe-assignment */
/* eslint-disable ts/strict-boolean-expressions */
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Dialog, DialogContent, ErrorContainer, Form, FormCheckbox, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, FormMultipleInput, FormNumberInput, Heading, IconBin, IconCreate, IconCross, IconInfo, IconInputSearch, IconLock, IconRestartList, IconTick, IconUser, IconUserEdit, RadioGroup, RadioGroupItem, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Text } from "@sentricmusic/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useGetMyAccounts, useGetMySong, useGetMyWriters } from "@/api/queries";
// Site configuration
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
// @ts-expect-error - Legacy JSX component without types
import ChooseAccountModal from "@/components/ChooseAccountModal";
import RebrandButtonLink from "@/components/RebrandButtonLink";
// @ts-expect-error - Legacy JSX component without types
import useSongsCreateUpdateTour from "@/components/tour/SongsCreateUpdateTour";
// For styling consistency with original design
import useGetColours from "@/helpers/getColours";
import { IconInfoWithTooltip } from "@/newComponents/icon/Icon";
import useAppStore from "@/store/appStateZustand";
// Import existing hooks and types
import { useUIStateStore } from "@/store/uiStateStore";
import { isValidSpotifyTrackUrl } from "@/utils/spotifyUrlUtils";

import type { Recording, WriterForSelection } from "./components";
import type { SongFormSchemaData } from "./utils";

import { CreateRecordingForm, CreateWriterModal, SelectExistingWritersModal } from "./components";
import SingleWriterWarningModal from "./components/SingleWriterWarningModal";
import { useSongOperations } from "./hooks/useSongOperations";
import { useSpotifyOperations } from "./hooks/useSpotifyOperations";
import useSongCreationStore from "./store/songCreationStore";
import { clearErrorMessages, createSongFormSchema, resetFormAndStore, validateSongBusinessLogic } from "./utils";

// Type for API recording data
type APIRecording = {
  title?: string;
  performingartist?: string;
  isrc?: string;
  recordlabel?: string;
  uicbarcode?: string;
  releasedate?: string;
  spotifyidentifier?: string;
  spotifypopularity?: number;
  spotifypopularitylastupdatedutc?: string;
  isdeleted?: boolean;
  recordingidentifier?: string;
  recordingamendmentidentifier?: string;
};

// Type for alternative title data from API
type APIAlternativeTitle = {
  title: string;
  isdeleted?: boolean;
  alternativetitleidentifier?: string;
  alternativetitleamendmentidentifier?: string;
};

// Type for suggestion data coming from external sources
type SuggestionData = {
  title: string;
  duration: number;
  artists: string;
  isrc?: string;
  release_date?: string; // Updated to match API response
  spotify_song_id?: string;
  popularity?: number;
  id: string;
};

// Component props type
type SongsCreateUpdateProps = {
  id?: string;
  closeForm?: () => void;
  suggestion?: SuggestionData | null;
  onComplete?: () => void;
  aac?: string | null;
};

// Default function to prevent inline arrow functions
function defaultOnComplete() {}

export default function SongsCreateUpdate({
  id,
  closeForm,
  suggestion = null,
  onComplete = defaultOnComplete,
  aac = null,
}: SongsCreateUpdateProps) {
  const { t } = useTranslation(["songs", "common", "regex"]);
  const navigate = useNavigate();
  const location = { search: useRouterState({ select: (s): string => s.location.searchStr }), pathname: useRouterState({ select: (s): string => s.location.pathname }) };
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Site configuration and colors for styling consistency
  const { highlightColour2 } = useGetColours();

  const isUpdate = Boolean(id);

  const flags = useFlags();
  const showTourFlag: boolean = Boolean(flags["pd-show-tutorial"]);

  const startTour = useSongsCreateUpdateTour();

  const startSongsCreateUpdateTour = () => {
    startTour();
  };

  // Get selected account
  let selectedAccount = useAppStore(s => s.selectedAccount) as string;
  if (aac !== null && aac !== undefined && aac.length > 0) {
    selectedAccount = aac;
  }

  // State for error messages
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [spotifyLinkErrorMessage, setSpotifyLinkErrorMessage] = useState("");

  // State for pending operations
  const [pending, setPending] = useState(false);

  // State for locked song
  const [isLocked, setIsLocked] = useState(false);

  // State for create/edit recording modal
  const [showCreateRecordingModal, setShowCreateRecordingModal] = useState(false);
  const [editingRecordingIndex, setEditingRecordingIndex] = useState<number | null>(null);

  // State for editing writer
  const [editingWriterCode, setEditingWriterCode] = useState<string | null>(null);

  // State for single writer warning modal
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<(() => void) | null>(null);

  // State to track alternative titles with their identifiers for updates
  const [alternativeTitlesWithIds, setAlternativeTitlesWithIds] = useState<APIAlternativeTitle[]>([]);

  // Ref to track if we've already fetched from URL to prevent duplicate fetches
  const hasLoadedFromUrl = useRef(false);

  // --- ACCOUNT SELECTION FOR DEEP LINKS ---
  const myAccounts = useGetMyAccounts();
  const [showAccountModal, setShowAccountModal] = useState(false);
  const hasCheckedDeepLinkAccount = useRef(false);

  // Check if this is a deep link with spotifyId and user has multiple accounts
  useEffect(() => {
    // Skip if already checked, updating existing song, or accounts not loaded yet
    if (hasCheckedDeepLinkAccount.current || isUpdate || !myAccounts.isSuccess || !Array.isArray(myAccounts.data?.artistaccounts)) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const spotifyId = searchParams.get("spotifyId");

    // Only show modal if deep linking with multiple accounts
    if (spotifyId != null && spotifyId !== "" && myAccounts.data.artistaccounts.length > 1) {
      hasCheckedDeepLinkAccount.current = true;
      setShowAccountModal(true);
    }
    else {
      hasCheckedDeepLinkAccount.current = true;
    }
  }, [location.search, isUpdate, myAccounts.isSuccess, myAccounts.data?.artistaccounts]);

  // Writer modal state from store
  const {
    selectedWriters,
    showSelectWritersModal,
    showCreateWriterModal,
    getTotalShares,
    hasRightToCollect,
    setSelectedWriters,
    addWriter,
    removeWriter,
    updateWriterShare,
    updateWriterRole,
    updateWriterRightToCollect,
    distributeSharesEqually,
    setShowSelectWritersModal,
    setShowCreateWriterModal,
    clearState,
    initializeFromExisting,
    setSongData,
    songData,
    writers,
    addRecording,
    removeRecording,
    updateRecording,
  } = useSongCreationStore();

  // Clear state when component mounts if we're transitioning from edit to create
  // or from create to edit to prevent state pollution
  useEffect(() => {
    const currentPath = location.pathname;
    const isCreatePath = currentPath === "/songs/create";
    const isModalEdit = Boolean(id) && Boolean(closeForm);
    // If we're on the create path (not a modal), clear state to start fresh
    if (isCreatePath) {
      clearState();
    }
    // If we're editing in a modal and there's no suggestion, prepare for edit mode
    else if (isModalEdit && !suggestion) {
      // Don't clear state here - let the song data loading effect handle initialization
    }
  }, [location.pathname, id, closeForm, suggestion, clearState]);

  // Get Spotify data from store with proper typing
  const spotifyResult = songData.spotifyResult ?? null;
  const spotifyURL: string = songData.spotifyURL ?? "";

  // Type guard to check if object has an id property
  const hasId = (obj: unknown): obj is { id: string } => {
    return typeof obj === "object" && obj !== null && "id" in obj && typeof (obj as { id: unknown }).id === "string";
  };

  // Helper function to safely extract Spotify track ID
  const getSpotifyTrackId = (result: typeof spotifyResult): string => {
    if (!result)
      return "";
    if (hasId(result)) {
      return result.id;
    }
    return "";
  };

  // Writer designation codes mapping (same as album version)
  const DESIGNATION_OPTIONS = [
    { value: 1, label: t("songs:author") ?? "Author" },
    { value: 2, label: t("songs:composer") ?? "Composer" },
    { value: 3, label: t("songs:composer-author") ?? "Composer/Author" },
  ];

  // Custom hooks
  const {
    createSongs,
    updateSong,
  } = useSongOperations({
    selectedAccount,
    onValidationError: (messages) => {
      setValidationErrorMessage(Array.isArray(messages) ? messages.join(", ") : messages);
    },
    onError: (message) => {
      setMutationErrorMessage(message);
    },
  });

  const {
    getSpotifyTrackByUrl,
    getSpotifyTrackById,
  } = useSpotifyOperations({
    onError: (message) => {
      setSpotifyLinkErrorMessage(message);
    },
  });

  // Fetch song data for updates
  const song = useGetMySong({
    aac: selectedAccount,
    wc: id !== null && id !== undefined ? id : null,
    enabled: Boolean(id),
  });

  // Fetch available writers for selection
  const writersQuery = useGetMyWriters({
    aac: selectedAccount,
  });

  useEffect(() => {
    if (song.isSuccess && id != null && id.trim() !== "" && song.data != null) {
      setIsLocked(song.data.islocked);
    }
  }, [song.isSuccess, id, song.data?.islocked, song.data]);

  const siteConfigData = useSiteConfigAuth();

  // Create the dynamic schema with current data - this needs to be reactive
  const songFormSchema = useMemo(() => {
    return createSongFormSchema(
      siteConfigData.data || undefined,
      selectedWriters,
      t,
    );
  }, [siteConfigData.data, selectedWriters, t]);

  // Create a schema key for forcing form recreation when needed
  const schemaKey = useMemo(() => {
    return `${selectedWriters.length}-${selectedWriters.map(w => w.writercode).join(",")}`;
  }, [selectedWriters]);

  // Form setup - initialize with store data including writers
  const form = useForm<SongFormSchemaData>({
    resolver: zodResolver(songFormSchema),
    mode: "onSubmit", // Only validate on submit, not on every change
    reValidateMode: "onChange", // Re-validate on change after first submit attempt
    defaultValues: {
      title: songData.title || "",
      durationMins: songData.durationMins ?? 0,
      durationSecs: songData.durationSecs ?? 0,
      alternativeTitles: songData.alternativeTitles ?? [],
      performingArtists: songData.performingArtists ?? [],
      isRemix: songData.isRemix ?? false,
      containsSamples: songData.containsSamples ?? false,
      writers: [], // Initialize with empty array, will be synced later
    },
  });

  // Update form validation when schema changes
  useEffect(() => {
    // Don't trigger validation immediately - only when user actually submits
    // The schema is updated but validation will happen when user interacts
  }, [songFormSchema, form, schemaKey]);

  // Extract spotifyId from URL query params and auto-fetch Spotify track data
  useEffect(() => {
    // Early exit conditions - check songData.title from store instead of form
    if (isUpdate || spotifyResult || suggestion || songData.title || hasLoadedFromUrl.current) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const spotifyId = searchParams.get("spotifyId");

    // Exit if no spotifyId in URL
    if (spotifyId == null || spotifyId.trim() === "") {
      return;
    }

    // Mark that we're loading to prevent duplicate fetches
    hasLoadedFromUrl.current = true;

    // Validate Spotify ID format (22 characters, base62 encoded)
    const isValidSpotifyId = /^[\da-z]{22}$/i.test(spotifyId);

    if (!isValidSpotifyId) {
      setSpotifyLinkErrorMessage(
        `Invalid Spotify track ID. Expected 22 characters, got ${spotifyId.length}. Please check the link or enter a Spotify URL manually.`,
      );
      return;
    }

    // Fetch and populate form with Spotify track data
    const fetchSpotifyTrack = async () => {
      setPending(true);
      setSpotifyLinkErrorMessage("");

      try {
        const track = await getSpotifyTrackById(spotifyId);

        // Handle case where track is not found
        if (!track) {
          setSpotifyLinkErrorMessage("Track not found on Spotify. Please check the ID or try entering a Spotify URL manually.");
          toast.error("Track not found on Spotify");
          return;
        }

        const durationMins = Math.floor(track.duration_ms / 1000 / 60);
        const durationSecs = Math.floor((track.duration_ms / 1000) % 60);
        const artists = track.artists.map((a: { name: string }) => a.name);

        // Create initial recording from Spotify data
        const initialRecording: Recording = {
          title: track.name,
          performingartist: artists[0] || "",
          isrc: track.external_ids?.isrc,
          releasedate: (track.album?.release_date != null && track.album.release_date.trim() !== "")
            ? (track.album.release_date.length === 4
                ? `${track.album.release_date}-01-01T00:00:00.000Z`
                : `${track.album.release_date}T00:00:00.000Z`)
            : undefined,
          spotifyidentifier: track.id,
          spotifypopularity: track.popularity,
          spotifypopularitylastupdatedutc: new Date().toISOString(),
          recordingidentifier: undefined,
          recordingamendmentidentifier: undefined,
        };

        // Set form values
        form.setValue("title", track.name);
        form.setValue("durationMins", durationMins);
        form.setValue("durationSecs", durationSecs);
        form.setValue("performingArtists", artists);

        // Update store with Spotify data and recording - preserve existing data like handleSpotifyURLSubmit
        setSongData({
          ...songData, // Preserve existing data
          title: track.name,
          durationMins,
          durationSecs,
          alternativeTitles: form.getValues("alternativeTitles") ?? [],
          performingArtists: artists,
          isRemix: form.getValues("isRemix") ?? false,
          containsSamples: form.getValues("containsSamples") ?? false,
          spotifyResult: track,
          spotifyURL: `https://open.spotify.com/track/${track.id}`,
          recordings: [initialRecording],
        });

        // Show success toast
        toast.success(
          `Song details loaded from Spotify: "${track.name}" by ${artists.join(", ")}`,
        );
      }
      catch (error) {
        console.error("Error fetching Spotify track from URL:", error);
        setSpotifyLinkErrorMessage(
          "Could not load track from Spotify. Please try entering the Spotify URL manually.",
        );
        toast.error("Failed to load song from Spotify. Please try again.");
      }
      finally {
        setPending(false);
      }
    };

    void fetchSpotifyTrack();
    // setPending, setSpotifyLinkErrorMessage are stable setState functions
    // toast is omitted as it's a side-effect utility that doesn't affect the effect logic
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isUpdate, spotifyResult, suggestion, songData.title, getSpotifyTrackById, form, setSongData]);

  // Save form data to store as user types (for new songs only)
  useEffect(() => {
    if (!isUpdate) {
      const subscription = form.watch((formData) => {
        // Get current recordings from store to avoid stale closure
        const currentStoreState = useSongCreationStore.getState();
        const currentRecordings = currentStoreState.songData.recordings;

        // Save current form state to store
        setSongData({
          title: formData.title ?? "",
          durationMins: formData.durationMins ?? 0,
          durationSecs: formData.durationSecs ?? 0,
          alternativeTitles: (formData.alternativeTitles ?? []).filter((title): title is string => title !== undefined),
          performingArtists: (formData.performingArtists ?? []).filter((artist): artist is string => artist !== undefined && artist.trim() !== ""),
          isRemix: formData.isRemix ?? false,
          containsSamples: formData.containsSamples ?? false,
          // Keep existing Spotify data and recordings
          spotifyResult,
          spotifyURL,
          recordings: currentRecordings, // Preserve existing recordings from store
        });
      });

      return () => subscription.unsubscribe();
    }
  }, [form, isUpdate, setSongData, spotifyResult, spotifyURL]); // Handle song data loading for updates

  useEffect(() => {
    if (song.isSuccess && Boolean(id) && Boolean(song.data) && writersQuery.isSuccess && writersQuery.data != null) {
      const songData = song.data;

      // Process recordings from the song data
      const songRecordings: Recording[] = Array.isArray(songData.recordings)
        ? songData.recordings
            .filter((r: APIRecording) => !r.isdeleted) // Only include non-deleted recordings
            .map((r: APIRecording) => ({
              title: r.title ?? "",
              performingartist: r.performingartist ?? "",
              isrc: r.isrc,
              recordlabel: r.recordlabel,
              uicbarcode: r.uicbarcode,
              releasedate: r.releasedate,
              spotifyidentifier: r.spotifyidentifier,
              spotifypopularity: r.spotifypopularity,
              spotifypopularitylastupdatedutc: r.spotifypopularitylastupdatedutc,
              recordingidentifier: r.recordingidentifier,
              recordingamendmentidentifier: r.recordingamendmentidentifier,
            }))
        : [];

      const formData = {
        title: songData.title || "",
        durationMins: songData.durationmins || 0,
        durationSecs: songData.durationsecs || 0,
        alternativeTitles: Array.isArray(songData.alternativetitles)
          ? songData.alternativetitles
              .filter((a: APIAlternativeTitle) => !a.isdeleted)
              .map((a: APIAlternativeTitle) => a.title)
          : [],
        performingArtists: Array.isArray(songData.performingartists)
          ? songData.performingartists
              .filter((p: { isdeleted?: boolean }) => !p.isdeleted)
              .map((p: { performingartistname: string }) => p.performingartistname)
          : [],
        isRemix: songData.isremix || false,
        containsSamples: songData.containssamples || false,
        writers: Array.isArray(songData.writersplits)
          ? songData.writersplits
              .filter((w: { isdeleted?: boolean }) => w.isdeleted !== true) // Filter out deleted writers
              .map((w: {
                writercode: string;
                writername?: string;
                firstname?: string;
                lastname?: string;
                writershare?: number;
                writerdesignationcode?: string;
                righttocollect?: boolean;
              }) => ({
                writercode: w.writercode,
                writername: w.writername ?? `${w.firstname ?? ""} ${w.lastname ?? ""}`.trim(),
                writershare: w.writershare ?? 0,
                writerdesignationcode: Number.parseInt(w.writerdesignationcode ?? "3", 10),
                righttocollect: w.righttocollect ?? false,
              }))
          : [],
      };

      // Store alternative titles with their identifiers for later use
      const alternativeTitlesWithIdentifiers = Array.isArray(songData.alternativetitles)
        ? songData.alternativetitles
            .filter((a: APIAlternativeTitle) => !a.isdeleted)
            .map((a: APIAlternativeTitle) => ({
              title: a.title,
              alternativetitleidentifier: a.alternativetitleidentifier,
              alternativetitleamendmentidentifier: a.alternativetitleamendmentidentifier,
            }))
        : [];
      setAlternativeTitlesWithIds(alternativeTitlesWithIdentifiers);

      form.reset(formData);

      // Initialize store with writer data
      const storeWriters = Array.isArray(songData.writersplits)
        ? songData.writersplits
            .filter((w: { isdeleted?: boolean }) => w.isdeleted !== true) // Filter out deleted writers
            .map((w: {
              writercode: string;
              writername?: string;
              firstname?: string;
              lastname?: string;
              writershare?: number;
              writerdesignationcode?: string;
              righttocollect?: boolean;
            }) => ({
              writercode: w.writercode,
              writername: w.writername ?? `${w.firstname ?? ""} ${w.lastname ?? ""}`.trim(),
              writershare: w.writershare ?? 0,
              writerdesignationcode: Number.parseInt(w.writerdesignationcode ?? "3", 10),
              righttocollect: w.righttocollect ?? false,
            }))
        : [];

      // Fix: Properly lookup writer details from writers API (like JSX version)
      const storeSelectedWriters = Array.isArray(songData.writersplits)
        ? songData.writersplits
            .filter((w: { isdeleted?: boolean }) => w.isdeleted !== true) // Filter out deleted writers
            .map((w: {
              writercode: string;
              writername?: string;
              firstname?: string;
              lastname?: string;
              caeipinumber?: string;
            }) => {
              // Lookup the full writer details from the writers API (just like JSX version)
              const writerFromAPI = writersQuery.data.writers.find(
                (writer: { writercode: string }) => writer.writercode === w.writercode,
              );

              return {
                writercode: w.writercode,
                writername: w.writername ?? `${w.firstname ?? ""} ${w.lastname ?? ""}`.trim(),
                // Use CAE/IPI from writers API if available, fallback to song data
                caeipinumber: writerFromAPI?.caeipinumber ?? w.caeipinumber ?? null,
                // Include date of birth for validation
                dateofbirth: writerFromAPI?.dateofbirth ?? null,
                // Include IMRO registration status for validation
                writerexternalregistrationstatus: writerFromAPI?.writerexternalregistrationstatus ?? null,
              };
            })
        : [];

      // Initialize the store with the existing data including recordings
      initializeFromExisting(
        {
          title: formData.title,
          durationMins: formData.durationMins,
          durationSecs: formData.durationSecs,
          alternativeTitles: formData.alternativeTitles,
          performingArtists: formData.performingArtists,
          isRemix: formData.isRemix,
          containsSamples: formData.containsSamples,
          recordings: songRecordings, // Include recordings from song data
          spotifyResult: null, // No Spotify data for existing songs
          spotifyURL: "",
        },
        storeWriters,
        storeSelectedWriters,
      );
    }
  }, [song.isSuccess, song.data, id, form, initializeFromExisting, writersQuery.isSuccess, writersQuery.data]);

  // Handle suggestion data
  useEffect(() => {
    if (suggestion !== null && suggestion !== undefined && !form.getValues().title) {
      const durationMins = Math.floor(suggestion.duration / 1000 / 60);
      const durationSecs = Math.floor((suggestion.duration / 1000) % 60);
      const artists = suggestion.artists.split(",").map((a: string) => a.replace(/&#44;/g, ","));

      // Create recording from suggestion data
      const suggestionRecording: Recording = {
        title: suggestion.title,
        performingartist: artists[0] || "",
        isrc: suggestion.isrc,
        releasedate: (suggestion.release_date != null && suggestion.release_date.trim() !== "") ? (
          suggestion.release_date.length === 4
            ? `${suggestion.release_date}-01-01T00:00:00.000Z`
            : suggestion.release_date.includes("T")
              ? suggestion.release_date
              : `${suggestion.release_date}T00:00:00.000Z`
        ) : undefined,
        spotifyidentifier: suggestion.spotify_song_id,
        spotifypopularity: suggestion.popularity,
        spotifypopularitylastupdatedutc: (suggestion.spotify_song_id != null && suggestion.spotify_song_id.trim() !== "") ? new Date().toISOString() : undefined,
        // New recording from suggestion - no existing identifiers
        recordingidentifier: undefined,
        recordingamendmentidentifier: undefined,
      };

      form.reset({
        title: suggestion.title,
        durationMins,
        durationSecs,
        performingArtists: artists,
        alternativeTitles: [],
        isRemix: false,
        containsSamples: false,
        writers: [],
      });

      // Set suggestion data in store with the recording
      setSongData({
        title: suggestion.title,
        durationMins,
        durationSecs,
        alternativeTitles: [],
        performingArtists: artists,
        isRemix: false,
        containsSamples: false,
        spotifyResult: null, // Suggestions are not Spotify results
        spotifyURL: "",
        recordings: [suggestionRecording], // Add the recording from suggestion
      });

      // Don't clear state here as it would remove the recording we just added
    }
  }, [suggestion, form, setSongData]);

  // Sync form writers with store writers - keep them in sync at all times
  useEffect(() => {
    const currentFormWriters = form.getValues().writers;

    // Always keep form writers in sync with store writers
    // This handles updates to writer shares, designation, rights to collect, etc.
    if (writers.length > 0) {
      // Check if form writers are different from store writers
      const needsSync = writers.length !== currentFormWriters.length
        || writers.some((storeWriter, index) => {
          const formWriter = currentFormWriters[index];
          if (index >= currentFormWriters.length)
            return true;
          return formWriter.writercode !== storeWriter.writercode
            || formWriter.writershare !== storeWriter.writershare
            || formWriter.writerdesignationcode !== storeWriter.writerdesignationcode
            || formWriter.righttocollect !== storeWriter.righttocollect;
        });

      if (needsSync) {
        form.setValue("writers", writers);
        // Trigger validation to clear any writer-related errors immediately
        if (form.formState.isSubmitted) {
          void form.trigger("writers");
        }
      }
    }
    else if (currentFormWriters.length > 0) {
      // Clear form writers if store is empty
      form.setValue("writers", []);
      // Trigger validation to show writer errors immediately if form was submitted
      if (form.formState.isSubmitted) {
        void form.trigger("writers");
      }
    }
  }, [writers, form]);

  // Error redirect
  if ((song.isError || song.data === null) && isUpdate) {
    void navigate({ to: "/songs" });
  }

  // Handle Spotify URL submission with validation
  const handleSpotifyURLSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate Spotify URL format (supports internationalized URLs)
    if (!spotifyURL || !isValidSpotifyTrackUrl(spotifyURL)) {
      setSpotifyLinkErrorMessage(t("regex:spotify-url-invalid") ?? "Please enter a valid Spotify track URL");
      return;
    }

    setPending(true);
    setSpotifyLinkErrorMessage(""); // Clear any previous errors

    try {
      const track = await getSpotifyTrackByUrl(spotifyURL);
      if (track) {
        const durationMins = Math.floor(track.duration_ms / 1000 / 60);
        const durationSecs = Math.floor((track.duration_ms / 1000) % 60);
        const artists = track.artists.map((a: { name: string }) => a.name);

        // Create initial recording from Spotify data
        const initialRecording: Recording = {
          title: track.name,
          performingartist: artists[0] || "",
          isrc: track.external_ids?.isrc,
          releasedate: ((track.album?.release_date) != null) && track.album.release_date.trim() !== ""
            ? (track.album.release_date.length === 4
                ? `${track.album.release_date}-01-01T00:00:00.000Z`
                : `${track.album.release_date}T00:00:00.000Z`)
            : undefined,
          spotifyidentifier: track.id,
          spotifypopularity: track.popularity,
          spotifypopularitylastupdatedutc: new Date().toISOString(),
          // New recording from Spotify - no existing identifiers
          recordingidentifier: undefined,
          recordingamendmentidentifier: undefined,
        };

        form.setValue("title", track.name);
        form.setValue("durationMins", durationMins);
        form.setValue("durationSecs", durationSecs);
        form.setValue("performingArtists", artists);

        // Update store with both Spotify result and all form data
        setSongData({
          ...songData, // Preserve existing data
          title: track.name,
          durationMins,
          durationSecs,
          alternativeTitles: form.getValues("alternativeTitles") ?? [],
          performingArtists: artists,
          isRemix: form.getValues("isRemix") ?? false,
          containsSamples: form.getValues("containsSamples") ?? false,
          spotifyResult: track,
          spotifyURL,
          recordings: [initialRecording], // Add initial recording
        });
      }
    }
    catch {
      // Error is handled by the hook
    }
    finally {
      setPending(false);
    }
  };

  // Wrapper for Spotify button to handle async
  const handleSpotifyButtonClick = () => {
    void handleSpotifyURLSubmit({ preventDefault: () => {} } as React.FormEvent);
  };

  // Remove Spotify selection
  const removeSpotify = () => {
    form.reset({
      title: "",
      durationMins: 0,
      durationSecs: 0,
      alternativeTitles: [],
      performingArtists: [],
      isRemix: false,
      containsSamples: false,
      writers: [],
    });

    // Clear all data from store including Spotify data
    setSongData({
      title: "",
      durationMins: 0,
      durationSecs: 0,
      alternativeTitles: [],
      performingArtists: [],
      isRemix: false,
      containsSamples: false,
      spotifyResult: null,
      spotifyURL: "",
      recordings: [], // Clear recordings too
    });

    // Clear any Spotify error messages
    setSpotifyLinkErrorMessage("");
  };

  // Writer management functions using Zustand store
  const handleWritersSelected = (newWriters: WriterForSelection[]) => {
    const updatedWriters = [...selectedWriters, ...newWriters];
    setSelectedWriters(updatedWriters);

    // Update form and store with new writers
    const currentWriters = form.getValues().writers;
    const newFormWriters = newWriters.map(writer => ({
      writercode: writer.writercode,
      writername: writer.writername,
      writershare: 0, // Default share, user will need to adjust
      writerdesignationcode: 3, // Default designation
      righttocollect: false,
    }));

    // Add to both form and store
    form.setValue("writers", [...currentWriters, ...newFormWriters]);
    newFormWriters.forEach(writer => addWriter(writer));
    setShowSelectWritersModal(false);
  };

  const handleWriterCreated = (writer: WriterForSelection) => {
    const updatedWriters = [...selectedWriters, writer];
    setSelectedWriters(updatedWriters);

    // Update form and store with new writer
    const currentWriters = form.getValues().writers;
    const newWriter = {
      writercode: writer.writercode,
      writername: writer.writername,
      writershare: 0, // Default share, user will need to adjust
      writerdesignationcode: 3, // Default designation
      righttocollect: false,
    };

    form.setValue("writers", [...currentWriters, newWriter]);
    addWriter(newWriter);
    setShowCreateWriterModal(false);
  };

  const handleWriterUpdated = (updatedWriter: WriterForSelection) => {
    // Update the selectedWriters array
    const updatedSelectedWriters = selectedWriters.map(w =>
      w.writercode === updatedWriter.writercode ? updatedWriter : w,
    );
    setSelectedWriters(updatedSelectedWriters);

    // Update the form writers array (preserve shares and designations)
    const currentFormWriters = form.getValues().writers;
    const updatedFormWriters = currentFormWriters.map(w =>
      w.writercode === updatedWriter.writercode
        ? { ...w, writername: updatedWriter.writername }
        : w,
    );
    form.setValue("writers", updatedFormWriters);

    // Update the store writers array (preserve shares and designations)
    const currentStoreWriters = useSongCreationStore.getState().writers;
    const updatedStoreWriters = currentStoreWriters.map(w =>
      w.writercode === updatedWriter.writercode
        ? { ...w, writername: updatedWriter.writername }
        : w,
    );

    // Update store with the modified writers
    useSongCreationStore.setState({ writers: updatedStoreWriters });

    setShowCreateWriterModal(false);
  };

  const handleRemoveWriter = (writercode: string) => {
    removeWriter(writercode);

    const currentWriters = form.getValues().writers;
    const filteredWriters = currentWriters.filter(w => w.writercode !== writercode);
    form.setValue("writers", filteredWriters);
  };

  // Use store's distribute function
  const handleDistributeSharesEqually = () => {
    distributeSharesEqually();

    // Sync with form - get fresh state from store
    const currentStoreWriters = useSongCreationStore.getState().writers;
    form.setValue("writers", currentStoreWriters);
  };

  // Recording management functions
  const handleRecordingCreated = (recording: Recording) => {
    if (editingRecordingIndex !== null) {
      // Update existing recording
      updateRecording(editingRecordingIndex, recording);
    }
    else {
      // Add new recording
      addRecording(recording);
    }
    setShowCreateRecordingModal(false);
    setEditingRecordingIndex(null);
  };

  // Edit recording management functions
  const handleEditRecording = (index: number) => {
    setEditingRecordingIndex(index);
    setShowCreateRecordingModal(true);
  };

  const handleCloseRecordingModal = () => {
    setShowCreateRecordingModal(false);
    setEditingRecordingIndex(null);
  };

  // Actual form submission logic
  const actualSubmit = async (data: SongFormSchemaData) => {
    try {
      const songPayload = {
        feature: "AddSong", // Indicate this is from the Add Song feature
        artistaccountcode: selectedAccount, // Required: Artist account code
        title: data.title,
        clientid: "dashboard", // Required: Client identifier
        durationmins: data.durationMins,
        durationsecs: data.durationSecs,
        isremix: data.isRemix,
        containssamples: data.containsSamples,
        ispublicdomain: false, // Required: Default to false for new songs
        alternativetitles: data.alternativeTitles.filter(title => title.trim().length > 0).map((title) => {
          // For updates, check if this alternative title existed before (has identifiers)
          const existingAltTitle = alternativeTitlesWithIds.find(alt => alt.title === title);
          if (existingAltTitle) {
            // Use existing identifiers for titles that already exist
            return {
              title,
              alternativetitleidentifier: existingAltTitle.alternativetitleidentifier ?? "00000000-0000-0000-0000-000000000000",
              alternativetitleamendmentidentifier: existingAltTitle.alternativetitleamendmentidentifier ?? "00000000-0000-0000-0000-000000000000",
            };
          }
          else {
            // For new alternative titles, provide empty GUIDs for both identifiers as per backend requirements
            return {
              title,
              alternativetitleidentifier: "00000000-0000-0000-0000-000000000000",
              alternativetitleamendmentidentifier: "00000000-0000-0000-0000-000000000000",
            };
          }
        }),
        performingartistnames: data.performingArtists.map(name => ({ name })),
        writersplits: writers
          .filter(w => Boolean(w.writercode) && w.writercode.trim() !== "") // Only include writers with valid writercode
          .map(w => ({
            writercode: w.writercode,
            writershare: w.writershare,
            writerdesignationcode: Number(w.writerdesignationcode),
            righttocollect: w.righttocollect,
          })),
        // Include recordings from store
        recordings: songData.recordings.map((r: Recording) => ({
          artistaccountcode: selectedAccount,
          title: r.title,
          performingartist: r.performingartist,
          isrc: (r.isrc != null && r.isrc.trim() !== "") ? r.isrc : undefined,
          recordlabel: (r.recordlabel != null && r.recordlabel.trim() !== "") ? r.recordlabel : undefined,
          uicbarcode: (r.uicbarcode != null && r.uicbarcode.trim() !== "") ? r.uicbarcode : undefined,
          releasedate: (r.releasedate != null && r.releasedate.trim() !== "") ? r.releasedate : undefined,
          spotifyidentifier: (r.spotifyidentifier != null && r.spotifyidentifier.trim() !== "") ? r.spotifyidentifier : undefined,
          spotifypopularity: (r.spotifypopularity != null && r.spotifypopularity > 0) ? r.spotifypopularity : undefined,
          spotifypopularitylastupdatedutc: (r.spotifypopularitylastupdatedutc != null && r.spotifypopularitylastupdatedutc.trim() !== "") ? r.spotifypopularitylastupdatedutc : undefined,
          // Use existing identifiers if they exist, otherwise use empty GUIDs for new recordings
          recordingidentifier: r.recordingidentifier ?? "00000000-0000-0000-0000-000000000000",
          recordingamendmentidentifier: r.recordingamendmentidentifier ?? "00000000-0000-0000-0000-000000000000",
        })),
      };

      if (isUpdate) {
        await updateSong({
          ...songPayload,
          workcode: song.data?.workcode ?? "",
          etag: song.data?.etag ?? "",
          iswc: song.data?.iswc,
        });

        toast.success(t("songs:song-updated-successfully"));

        // Reset form and store to empty state after successful update
        resetFormAndStore(form.reset, setSongData, clearState, setSpotifyLinkErrorMessage);
        setAlternativeTitlesWithIds([]); // Clear alternative titles with IDs
        if (closeForm) {
          // When editing in a modal, just close the modal - the list will auto-refresh via cache invalidation
          closeForm();
        }
        else {
          // When not in modal mode, navigate to songs list
          void navigate({ to: "/songs" });
        }
      }
      else {
        await createSongs(songPayload);
        toast.success(t("songs:song-created-successfully"));

        // Reset form and store to empty state for new songs
        resetFormAndStore(form.reset, setSongData, clearState, setSpotifyLinkErrorMessage);
        setAlternativeTitlesWithIds([]); // Clear alternative titles with IDs

        // Handle onComplete for suggestions or redirect to songs list
        if (suggestion !== null && suggestion !== undefined) {
          // For suggestions, call onComplete to handle suggestion removal/update
          onComplete();
          // Also close the form/dropdown if closeForm is available
          if (closeForm) {
            closeForm();
          }
        }
        else if (closeForm) {
          // When creating in a modal, just close the modal - the list will auto-refresh via cache invalidation
          closeForm();
        }
        else {
          // When not in modal mode, navigate to songs list
          void navigate({ to: "/songs" });
        }
      }
    }
    catch {
      // Error handling is done in the hooks
    }
    finally {
      setPending(false);
    }
  };

  // Check for single writer warning before submission
  const checkWriterCountAndProceed = async (data: SongFormSchemaData) => {
    if (
      writers.length === 1
      && localStorage.getItem("showWriterWarning") !== "false"
    ) {
      setPendingSubmission(() => async () => actualSubmit(data));
      setIsWarningModalOpen(true);
      setPending(false); // Cancel pending state since we're showing modal
    }
    else {
      await actualSubmit(data);
    }
  };

  // Handle warning modal confirmation
  const handleWarningConfirm = () => {
    setIsWarningModalOpen(false);
    if (pendingSubmission) {
      void pendingSubmission();
      setPendingSubmission(null);
    }
  };

  // Handle warning modal close
  const handleWarningClose = () => {
    setIsWarningModalOpen(false);
    setPendingSubmission(null);
    setPending(false); // Reset pending state
  };

  // Form submission wrapper - this is what React Hook Form calls
  const onSubmit = async (data: SongFormSchemaData) => {
    setPending(true);
    clearErrorMessages(setSpotifyLinkErrorMessage, setMutationErrorMessage, setValidationErrorMessage);

    // Check business logic validation before proceeding
    const businessValidation = validateSongBusinessLogic(data, siteConfigData.data || undefined, selectedWriters, t, isTunecore);
    if (!businessValidation.isValid) {
      setValidationErrorMessage(businessValidation.issues.join(", "));
      setPending(false);
      return;
    }

    // Zod validation is handled automatically by React Hook Form
    // If we get here, the form is valid according to the schema
    await checkWriterCountAndProceed(data);
  };

  // Form submission wrapper to handle async properly
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void form.handleSubmit(onSubmit)(e);
  };

  // Business logic validation state for UI feedback
  // Only handles complex validations that Zod cannot express
  const formValues = form.watch(); // Watch all form values for reactivity
  const businessLogicValidation = useMemo(() => {
    return validateSongBusinessLogic(formValues, siteConfigData.data || undefined, selectedWriters, t, isTunecore);
  }, [formValues, siteConfigData.data, selectedWriters, t, isTunecore]);

  // Submit button logic - check both form validation (Zod) and business logic validation after first submit
  const canSubmit = !pending && (!form.formState.isSubmitted || (form.formState.isValid && businessLogicValidation.isValid));

  return (
    <div className="flex flex-wrap lg:min-h-screen-minus-header">
      <div
        className={`w-full ${
          location.pathname === "/songs" ? "lg:p-6 p-4" : ""
        } ${
          location.pathname === "/songs/create"
            ? ""
            : "lg:p-6 p-4 lg:min-h-screen-minus-header"
        } ${isTunecore ? "text-white" : ""}`}
      >
        {suggestion === null && (
          <div className="flex flex-col items-start gap-4 mb-5 sm:items-baseline">

            <div className="flex flex-row items-start justify-between w-full">
              <div className="flex items-end justify-between gap-5">
                <Heading className="font-semibold" as="h3">
                  {isUpdate ? t("songs:update-header") : t("songs:add-header")}
                  {" "}
                  {t("songs:song")}
                </Heading>
                {isLocked && (
                  <IconLock size="xl" className="mb-1 stroke-ink-secondary" />
                )}
                {showTourFlag && (
                  <RebrandButtonLink onClick={startSongsCreateUpdateTour}>
                    <div className="flex items-center gap-2 mb-1">
                      {t("tutorial:need-help")}
                      {" "}
                      <IconInfo className="w-5 h-5 mb-1 stroke-ink-accent" />
                    </div>
                  </RebrandButtonLink>
                )}
              </div>

              <div className="flex flex-row items-center gap-4">

                {!isUpdate && (songData.title || selectedWriters.length > 0) && (
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      resetFormAndStore(form.reset, setSongData, clearState, setSpotifyLinkErrorMessage);
                      setAlternativeTitlesWithIds([]); // Clear alternative titles with IDs
                    }}
                    tooltip
                    // tooltipText="Start Over"
                    tooltipText={t("songs:start-over-tooltip") ?? "Start Over"}
                    icon={<IconRestartList size="lg" className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "group-hover:stroke-ink-accent"}`} />}
                  />
                )}
                {!isUpdate && (
                  <Button
                    tooltip
                    tooltipText={t("common:cancel") ?? "Cancel"}
                    icon={<IconCross size="lg" className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "group-hover:stroke-ink-accent"}`} />}
                    variant="link"
                    className={isTunecore ? "border-layer-inverse text-white hover:bg-layer-inverse" : ""}
                    onClick={() => {
                      if (suggestion !== null && suggestion !== undefined) {
                      // For suggestions, just close the form to return to suggestion selection
                        if (closeForm) {
                          closeForm();
                        }
                      }
                      else {
                      // For regular new song form, clear all data and navigate to songs page
                        resetFormAndStore(form.reset, setSongData, clearState, setSpotifyLinkErrorMessage);
                        setAlternativeTitlesWithIds([]); // Clear alternative titles with IDs
                        void navigate({ to: "/songs" });
                      }
                    }}
                  />
                )}
              </div>
            </div>

            {isLocked && (
              <Text as="p" className="font-bold">
                <span className="mr-1 text-ink-accent">*</span>
                {t("songs:locked-field-explanation")}
              </Text>
            )}
          </div>
        )}

        <div className={`${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "bg-white p-4 lg:p-6 rounded-lg text-black"}`}>

          {(id === null || id === undefined || (id && song.isSuccess && song.data !== null)) && (
            <Form {...form}>
              <form onSubmit={handleFormSubmit} className="space-y-8 xl:space-y-10">
                {/* Spotify Integration Section */}
                {!isUpdate && suggestion === null && flags["pd-spotify-recording-song-details-feature"] && (
                  <div
                    id="spotify-component"
                    className={`flex flex-col gap-6 shadow-md ${isTunecore ? "text-white md:px-14 md:py-10 p-4 rounded-lg bg-layer-inverse" : "bg-layer-raised p-4 md:px-14 md:py-10 rounded-lg"}`}
                  >
                    <div>
                      <Text as="subtitle">
                        {t("songs:import-from-spotify-header")}
                      </Text>
                      <Text>
                        {t("songs:import-from-spotify-text")}
                        {" "}
                        <span>
                          <a
                            className="underline hover:scale-105"
                            href="https://support.spotify.com/us/artists/article/finding-your-artist-url/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("songs:link-spotify-instructions-link")}
                          </a>
                        </span>
                      </Text>
                    </div>
                    <div>
                      {!spotifyResult && (
                        <div className="flex flex-col gap-4">
                          <div>
                            <FormInput
                              type="text"
                              placeholder={t("songs:link-spotify-paste-track-url") ?? "Paste Spotify Track URL here"}
                              className="w-full font-medium"
                              value={spotifyURL ?? ""}
                              onChange={e => setSongData({ ...songData, spotifyURL: e.target.value })}
                              hasError={!!spotifyLinkErrorMessage}
                            />
                            {spotifyLinkErrorMessage && (
                              <Text className="text-sm font-medium leading-snug text-ink-error">
                                {spotifyLinkErrorMessage}
                              </Text>
                            )}
                          </div>
                          <Button
                            className={`self-end group ${isTunecore
                              ? "text-white font-tunecore hover:text-ink-accent"
                              : "bg-[#1ED760] hover:bg-[#1DB954] text-black"}`}
                            type="button"
                            variant="darkSecondary"
                            icon={<IconInputSearch className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "stroke-black"}`} />}
                            disabled={pending}
                            onClick={handleSpotifyButtonClick}
                            label={pending ? t("songs:fetching-track") : t("songs:link-spotify-fetch-track")}
                          />
                        </div>
                      )}
                      {spotifyResult && (
                        <div className="flex flex-col gap-4">
                          <div className="relative w-full">
                            <iframe
                              title="preview"
                              src={`https://open.spotify.com/embed/track/${getSpotifyTrackId(spotifyResult)}`}
                              height="152"
                              allow="encrypted-media"
                              className="w-full rounded-xl"
                              sandbox="allow-scripts"
                            />
                          </div>
                          <Button
                            variant="darkSecondary"
                            onClick={removeSpotify}
                            className={`self-end group ${isTunecore
                              ? "text-white font-tunecore hover:text-ink-accent"
                              : "bg-[#1ED760] hover:bg-[#1DB954] text-black"}`}
                            icon={<IconBin className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "stroke-black"}`} />}
                            label={t("songs:remove-selection")}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Song Details Section */}
                <div className="w-full">
                  <Text className="mb-2" as="subtitle">
                    {t("songs:song-details-header")}
                  </Text>
                  {/*
                  <div className="flex flex-col w-full gap-6 lg:flex-row lg:gap-12"> */}

                  <div
                    className={`p-4 rounded-lg shadow-md flex flex-col w-full gap-6 lg:flex-row lg:gap-12 ${isTunecore
                      ? "bg-layer-inverse border border-stroke-subtle"
                      : "bg-layer-raised"}`}
                  >
                    <div className="flex flex-col w-full space-y-8" id="songsTitlesAlts">
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel required>
                              {t("songs:song-title") ?? "Song Title"}
                            </FormLabel>
                            <FormControl>
                              <FormInput
                                placeholder={t("songs:enter-song-title") ?? "Enter song title"}
                                {...field}
                                className="form-input"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="inline-flex items-center gap-2">
                        <div className="flex-1">
                          <FormField
                            control={form.control}
                            name="alternativeTitles"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("songs:alternative-titles") ?? "Alternative Titles"}
                                </FormLabel>
                                <FormControl>
                                  <FormMultipleInput
                                    values={field.value ?? []}
                                    onChange={field.onChange}
                                    // placeholder="Enter alternative titles"
                                    placeholder={t("songs:enter-alternative-titles") ?? "Enter alternative titles"}
                                    // addButtonText="Add Alternative Title"
                                    addButtonText={t("songs:add-alternative-title") ?? "Add Alternative Title"}
                                    minItems={0}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <IconInfoWithTooltip size="lg" className="mb-1.5" tooltip={t("regex:song-alternative-title-description-regex") ?? "An 'alternate title' is another version of a song; it may be a radio edit, album version, acoustic version, etc, however, the writer's splits are the same as the original. Add any alternative versions of this song via this section. Do not add them as their own registration."} />
                      </div>

                    </div>

                    <div className="flex flex-col w-full space-y-8">
                      <div className="flex gap-4" id="songsDurationSection">
                        <FormField
                          control={form.control}
                          name="durationMins"
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>
                                {t("common:mins-capital")}
                              </FormLabel>
                              <FormControl>
                                <FormNumberInput
                                  label=""
                                  min={0}
                                  max={120}
                                  className="w-full"
                                  placeholder="0"
                                  value={(field.value ?? 0).toString()}
                                  onChange={(e) => {
                                    const value = Number(e.target.value) || 0;
                                    field.onChange(value);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="durationSecs"
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>
                                {t("common:secs-capital")}
                              </FormLabel>
                              <FormControl>
                                <FormNumberInput
                                  label=""
                                  min={0}
                                  max={59}
                                  className="w-full"
                                  placeholder="0"
                                  value={(field.value ?? 0).toString()}
                                  onChange={(e) => {
                                    const value = Number(e.target.value) || 0;
                                    field.onChange(value);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div id="songPerformersSection">
                        <FormField
                          control={form.control}
                          name="performingArtists"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel required>{t("songs:performing-artists")}</FormLabel>
                              <FormControl>
                                <FormMultipleInput
                                  values={field.value ?? []}
                                  onChange={field.onChange}
                                  placeholder={t("songs:enter-performing-artists") ?? "Enter performing artist names"}
                                  addButtonText={t("songs:add-performing-artist") ?? "Add Performing Artist"}
                                  minItems={0}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Writers & Splits Section */}
                <div className="w-full">
                  <Text className="mb-2" as="subtitle">
                    {t("songs:writers")}
                    {" "}
                    &
                    {" "}
                    {t("songs:splits")}
                  </Text>
                  <div id="songWriterSplitsHeader">
                    {/* Progress Bar and Controls Section */}
                    <div
                      style={{ backgroundColor: isTunecore ? "#222222" : highlightColour2 }}
                      className={`${isTunecore
                        ? "text-white"
                        : "text-black"
                      } rounded-t-lg flex gap-5 md:gap-0 px-14 py-9 h-auto md:h-28 flex-wrap md:flex-nowrap`}
                    >
                      <div className="flex flex-col w-full md:w-1/2 justify-evenly">
                        <div>
                          <Text as="p">
                            {t("songs:writer-splits-assigned")}
                            :
                            <span className="ml-2 font-semibold">
                              {getTotalShares().toFixed(2)}
                              %
                            </span>
                          </Text>
                        </div>
                        <div className="relative w-full pt-1">
                          <div className={`flex h-3 overflow-hidden text-xs  border border-stroke-subtle rounded-full ${isTunecore ? "bg-layer-inverse" : "bg-layer-raised"}`}>
                            <div
                              style={{
                                width: `${Math.min(getTotalShares(), 100)}%`,
                              }}
                              className={`rounded-full flex flex-col text-center whitespace-nowrap text-white justify-center ${isTunecore
                                ? "bg-action-primary"
                                : "bg-layer-raised"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-center w-full md:w-1/2 md:justify-end lg:mb-0">
                        {selectedWriters.length > 0 && (
                          <Button
                            type="button"
                            variant={isTunecore ? "primary" : "secondary"}
                            onClick={handleDistributeSharesEqually}
                            label={t("songs:share-splits-equally")}
                          />
                        )}
                      </div>
                    </div>
                    {/* Validation Summary */}
                    <div className={`p-4 ${selectedWriters.length === 0 || !hasRightToCollect()
                      ? (isTunecore ? "text-white bg-[rgba(244,67,54,0.2)] font-tunecore border-none" : "bg-white border-x border-stroke-subtle")
                      : (isTunecore ? "text-white border-none" : "bg-white border-x border-stroke-subtle")
                    }`}
                    >
                      <div className="flex flex-col gap-2 text-sm">
                        {selectedWriters.length === 0 ? (
                          // Writer requirement validation
                          <div className="flex items-center justify-between">
                            <Text className={isTunecore ? "text-white" : "text-red-700"}>
                              <strong>{t("common:required-header")}</strong>
                              {": "}
                              <span>{t("songs:at-least-one-writer-required")}</span>
                            </Text>
                          </div>
                        ) : (
                          // Right to collect validation
                          <div className="flex items-center justify-between">
                            <Text className={
                              hasRightToCollect()
                                ? "text-ink-success"
                                : (isTunecore ? "text-white" : "text-red-600")
                            }
                            >
                              <strong>
                                {t("songs:right-to-collect-header")}
                                :
                              </strong>
                              {" "}
                              <span>{hasRightToCollect() ? t("songs:at-least-one-writer-rtc-success") : t("songs:at-least-one-writer-rtc")}</span>
                              {hasRightToCollect() && <IconTick className="inline-block w-5 h-5 ml-1 stroke-ink-success" />}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Writers Container */}
                  <div className={`${isTunecore ? "border-t border-stroke-subtle bg-layer-inverse" : "bg-layer-raised"} shadow-md rounded-b-lg`} id="songWritersContainer">
                    {/* Current Writers Display */}
                    {selectedWriters.length > 0 && (
                      <div className={`grid gap-4 p-4 xl:items-stretch ${selectedWriters.length === 1 ? "grid-cols-1" : "2xl:grid-cols-2"}`}>
                        {selectedWriters.map((writer, index) => (
                          <div
                            key={writer.writercode}
                            className={`rounded-lg p-4 flex flex-col h-full ${
                              isTunecore
                                ? "border border-stroke-subtle bg-layer-inverse"
                                : "border border-stroke-subtle bg-white"
                            }`}
                          >
                            {/* Single Line Layout for larger screens */}
                            <div className="flex flex-col flex-1 gap-2 lg:flex-row">
                              {/* Writer Info - Name and CAE/IPI */}
                              <div className={`flex flex-col ${selectedWriters.length === 1 ? "lg:w-1/3" : "lg:w-1/2"}`}>
                                <div>
                                  <Text as="subtitle">{writer.writername}</Text>
                                </div>
                                <div>
                                  <Text as="p">
                                    {t("songs:cae-ipi")}
                                    :
                                    {" "}
                                    {writer.caeipinumber != null && writer.caeipinumber.trim() !== "" ? (
                                      <>{writer.caeipinumber}</>
                                    ) : (
                                      <>{t("songs:no-cae-ipi")}</>
                                    )}
                                  </Text>

                                </div>

                              </div>

                              {/* Form Fields - Inline for larger screens */}
                              <div className={`flex flex-col justify-between gap-4 px-4 lg:border-l border-stroke-subtle ${selectedWriters.length === 1 ? "lg:w-2/3" : "lg:w-1/2"}`}>

                                {/* Role Field */}
                                <FormField
                                  control={form.control}
                                  name={`writers.${index}.writerdesignationcode`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>{t("songs:writer-role")}</FormLabel>
                                      <FormControl>
                                        <Select
                                          value={(field.value ?? 3).toString()}
                                          onValueChange={(value) => {
                                            const numericValue = Number.parseInt(value, 10);
                                            field.onChange(numericValue);
                                            const currentWriters = form.getValues().writers;
                                            currentWriters[index] = { ...currentWriters[index], writerdesignationcode: numericValue };
                                            form.setValue("writers", currentWriters);
                                            updateWriterRole(writer.writercode, numericValue);
                                          }}
                                        >
                                          <SelectTrigger className="min-w-36">
                                            <SelectValue placeholder={t("songs:select-role")} />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {DESIGNATION_OPTIONS.map(option => (
                                              <SelectItem key={option.value} value={option.value.toString()}>
                                                {option.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                {/* Share Field */}
                                <FormField
                                  control={form.control}
                                  name={`writers.${index}.writershare`}
                                  render={({ field }) => (
                                    <FormItem className="">
                                      <FormLabel>{t("songs:share-percentage")}</FormLabel>
                                      <FormControl>
                                        <FormNumberInput
                                          min={0}
                                          max={100}
                                          step={0.01}
                                          placeholder="0"
                                          className="w-full"
                                          value={(field.value ?? 0).toString()}
                                          onChange={(e) => {
                                            const value = Number.parseFloat(e.target.value) || 0;
                                            field.onChange(value);
                                            const currentWriters = form.getValues().writers;
                                            currentWriters[index] = { ...currentWriters[index], writershare: value };
                                            form.setValue("writers", currentWriters);
                                            updateWriterShare(writer.writercode, value);
                                          }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                {/* Right to Collect Field */}
                                <FormField
                                  control={form.control}
                                  name={`writers.${index}.righttocollect`}
                                  render={({ field }) => (
                                    <FormItem className="flex items-center">
                                      <FormControl>
                                        <FormCheckbox
                                          label={t("songs:right-to-collect-header") ?? "Right To Collect"}
                                          checked={field.value}
                                          onChange={(e) => {
                                            field.onChange(e.target.checked);
                                            const currentWriters = form.getValues().writers;
                                            currentWriters[index] = { ...currentWriters[index], righttocollect: e.target.checked };
                                            form.setValue("writers", currentWriters);
                                            updateWriterRightToCollect(writer.writercode, e.target.checked);
                                          }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                {/* Action Buttons */}
                                <div className="flex self-end gap-2">
                                  <Button
                                    type="button"
                                    variant="link"
                                    tooltip
                                    tooltipText={t("songs:edit-writer") ?? "Edit Writer"}
                                    onClick={() => {
                                      setEditingWriterCode(writer.writercode);
                                      setShowCreateWriterModal(true);
                                    }}
                                    icon={<IconUserEdit className={`h-5 w-5 ${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "group-hover:stroke-ink-accent"}`} />}
                                  />
                                  <Button
                                    type="button"
                                    variant="link"
                                    tooltip
                                    tooltipText={t("songs:remove-writer") ?? "Remove Writer"}
                                    onClick={() => handleRemoveWriter(writer.writercode)}
                                    icon={<IconBin className={`h-5 w-5 ${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "group-hover:stroke-ink-accent"}`} />}
                                  />
                                </div>
                              </div>

                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Writers Section */}
                    <div className={`p-4 flex flex-col gap-4 ${selectedWriters.length === 0 ? "rounded-b-lg" : ""}`}>
                      {selectedWriters.length === 0 && (
                        <div className="text-center">
                          <Text as="p">
                            {t("songs:add-writers-message") ?? "Use the buttons below to add writers for this song."}
                          </Text>
                        </div>
                      )}
                      <div className="flex flex-col gap-4 sm:flex-row">
                        <Button
                          type="button"

                          variant={isTunecore ? "primary" : "secondary"}
                          icon={<IconUser className={`${isTunecore ? "stroke-white" : "stroke-black"}`} />}
                          onClick={() => setShowSelectWritersModal(true)}
                          label={t("writers:select-existing-writer")}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant={isTunecore ? "primary" : "secondary"}
                          onClick={() => setShowCreateWriterModal(true)}
                          icon={<IconCreate className={`${isTunecore ? "stroke-white" : "stroke-black"}`} />}
                          label={t("songs:create-new-writer")}
                          className="flex-1"
                        />
                      </div>

                    </div>
                  </div>
                </div>

                {/* Recordings Section */}
                {!isUpdate && (
                  <div>
                    <Text className="mb-2" as="subtitle">
                      {t("songs:recordings", "Recordings")}
                    </Text>
                    {/* Recordings List */}
                    <div
                      className={`p-4 flex flex-col gap-4 shadow-md rounded-lg ${isTunecore
                        ? "bg-layer-inverse border border-stroke-subtle"
                        : "bg-layer-raised"}`}
                      id="songRecordingsContainer"
                    >

                      {songData.recordings.length > 0 ? (
                        <div className="space-y-4">
                          {songData.recordings.map((recording, index) => (
                            <div
                              className={`rounded-lg p-4 w-full flex flex-col h-full ${
                                isTunecore
                                  ? "border border-stroke-subtle bg-layer-inverse"
                                  : "border border-stroke-subtle bg-white"
                              }`}
                              key={`recording-${recording.title}-${recording.performingartist}-${(recording.spotifyidentifier != null && recording.spotifyidentifier.trim() !== "") ? recording.spotifyidentifier : `temp-${index}`}`}
                            >
                              <div className="flex items-start justify-between">

                                <div>
                                  <Text as="subtitle">{recording.title}</Text>
                                  <Text>
                                    {recording.performingartist}
                                  </Text>
                                </div>

                                <div className="flex flex-col gap-2 justify-between px-4 lg:w-1/2 lg:flex-row lg:border-l border-stroke-subtle">
                                  <div className="flex flex-col gap-1">
                                    <div>
                                      <Text>
                                        {t("songs:isrc-placeholder") ?? "ISRC"}
                                        {": "}
                                        {(recording.isrc != null) && recording.isrc.trim() !== "" && (
                                          <span>{recording.isrc}</span>
                                        )}
                                      </Text>
                                    </div>
                                    <div>
                                      <Text>
                                        {/* Performing Artist: */}
                                        {t("songs:performing-artist") ?? "Performing Artist"}
                                        {": "}
                                        {(recording.performingartist != null) && recording.performingartist.trim() !== "" && (
                                          <span>{recording.performingartist}</span>
                                        )}
                                      </Text>
                                    </div>
                                    <div>
                                      <Text>
                                        {t("songs:release-date") ?? "Release Date"}
                                        {": "}
                                        {(recording.releasedate != null) && recording.releasedate.trim() !== "" && (
                                          <span>{recording.releasedate.split("T")[0]}</span>
                                        )}
                                      </Text>
                                    </div>
                                  </div>

                                  <div className="flex flex-row self-end justify-between gap-2">
                                    <Button
                                      type="button"
                                      variant="link"
                                      className="self-start"
                                      tooltip
                                      tooltipText={t("songs:edit-recording") ?? "Edit Recording"}
                                      onClick={() => handleEditRecording(index)}
                                      icon={<IconCreate className={`h-5 w-5 ${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "group-hover:stroke-ink-accent"}`} />}
                                    />
                                    <Button
                                      type="button"
                                      variant="link"
                                      className="self-start"
                                      tooltip
                                      tooltipText={t("common:remove") ?? "Remove"}
                                      onClick={() => removeRecording(index)}
                                      icon={<IconBin className={`h-5 w-5 ${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "group-hover:stroke-ink-accent"}`} />}
                                    />
                                  </div>
                                </div>

                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <Text as="p" className="text-center">
                            {t("songs:no-recordings-message", "No recordings added yet. You can import one from Spotify above or create one manually.")}
                          </Text>
                        </>
                      )}
                      {/* Add Recording Button */}
                      <Button
                        type="button"
                        variant={isTunecore ? "primary" : "secondary"}
                        className="self-center"
                        onClick={() => setShowCreateRecordingModal(true)}
                        label={t("songs:create-new-recording", "Create New Recording")}
                      />
                      <Button
                        type="button"
                        variant="link"
                        className="self-center text-sm underline"
                        tooltip
                        label={t("songs:what-are-recordings") ?? "What are recordings?"}
                        tooltipText={t("songs:recordings-info") ?? "Recordings refer to the sound recording of a song, and you can have multiple recordings for the same song. Examples of this would include live or acoustic versions. This form is for registering the song composition for publishing, and you should be adding your recordings here, and including the 'Alternative Titles' above, not as a separate registration."}
                        onClick={() => setShowCreateRecordingModal(true)}
                      />
                    </div>

                  </div>
                )}
                <div>
                  <Text className="mb-2" as="subtitle">
                    {t("songs:remix-and-samples-header")}
                  </Text>
                  {/* Remix and Samples Section */}
                  <div
                    id="songsRemixSampleSection"
                    className={`p-4 flex flex-col md:flex-row gap-4 justify-between rounded-lg shadow-md ${isTunecore
                      ? "bg-layer-inverse border border-stroke-subtle"
                      : "bg-layer-raised"}`}
                  >
                    <FormField
                      control={form.control}
                      name="isRemix"
                      render={({ field }) => (
                        <FormItem className={`md:w-1/2 p-4 rounded-lg ${isTunecore
                          ? "bg-layer-inverse border border-stroke-subtle"
                          : "bg-white"}`}
                        >
                          <FormLabel>{String(t("songs:is-this-a-remix"))}</FormLabel>
                          <FormControl>
                            <RadioGroup
                              value={field.value ? "yes" : "no"}
                              onValueChange={value => field.onChange(value === "yes")}
                              className="flex flex-row gap-6"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="yes" id="isRemix-yes" />
                                <label htmlFor="isRemix-yes" className="text-sm font-medium cursor-pointer">
                                  {t("common:yes")}
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="no" id="isRemix-no" />
                                <label htmlFor="isRemix-no" className="text-sm font-medium cursor-pointer">
                                  {t("common:no")}
                                </label>
                              </div>

                              <IconInfoWithTooltip size="lg" className="mb-1.5" tooltip={t("songs:is-this-a-remix-description") ?? "A song that is a remix of another writer’s work must have written and contractual permission from all of the original songwriters in order to be registered for publishing. Please only continue if you have this permission"} />
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="containsSamples"
                      render={({ field }) => (
                        <FormItem className={`md:w-1/2 p-4 rounded-lg ${isTunecore
                          ? "bg-layer-inverse border border-stroke-subtle"
                          : "bg-white"}`}
                        >
                          <FormLabel>{t("songs:contains-samples-question")}</FormLabel>
                          <FormControl>
                            <RadioGroup
                              value={field.value ? "yes" : "no"}
                              onValueChange={value => field.onChange(value === "yes")}
                              className="flex flex-row gap-6"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="yes" id="containsSamples-yes" />
                                <label htmlFor="containsSamples-yes" className="text-sm font-medium cursor-pointer">
                                  {t("common:yes")}
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="no" id="containsSamples-no" />
                                <label htmlFor="containsSamples-no" className="text-sm font-medium cursor-pointer">
                                  {t("common:no")}
                                </label>

                              </div>
                              <IconInfoWithTooltip size="lg" className="mb-1.5" tooltip={t("songs:contains-samples-description") ?? "A song that contains samples must have written and contractual permission from all of the original songwriters in order to be registered for publishing. Please only continue if you have this permission"} />
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2">

                  {/* Error Messages */}
                  <ErrorContainer title={t("common:database-error") ?? "Database Error"} message={mutationErrorMessage} />
                  <ErrorContainer title={t("common:validation-error") ?? "Validation Error"} message={validationErrorMessage} />

                  {/* Combined Validation Errors - only show after first submit attempt */}
                  {form.formState.isSubmitted && (Object.keys(form.formState.errors).length > 0 || !businessLogicValidation.isValid) && (
                    <ErrorContainer
                      // title="Please fix the following issues:"
                      title={t("common:please-fix-following-issues") ?? "Please fix the following issues:"}
                      formErrors={form.formState.errors}
                      validationIssues={businessLogicValidation.isValid ? undefined : businessLogicValidation.issues}
                    />
                  )}
                </div>

                {/* Submit Buttons */}
                <div className="flex items-center justify-between max-md:flex-col max-md:gap-4" id="songsButtonsSection">
                  <Button
                    type="button"
                    onClick={() => {
                      if (isUpdate) {
                        // For updates, always use closeForm if available
                        if (closeForm) {
                          closeForm();
                        }
                        else {
                          void navigate({ to: "/songs" });
                        }
                      }
                      else if (suggestion !== null && suggestion !== undefined) {
                        // For suggestions, just close the form to return to suggestion selection
                        if (closeForm) {
                          closeForm();
                        }
                      }
                      else {
                      // For regular new song form, clear all data and navigate to songs page
                        resetFormAndStore(form.reset, setSongData, clearState);
                        setAlternativeTitlesWithIds([]); // Clear alternative titles with IDs
                        // For regular new song form, navigate to songs page
                        void navigate({ to: "/songs" });
                      }
                    }}

                    label={suggestion === null ? t("songs:cancel-and-return") : t("songs:cancel-and-discard")}
                    variant={isTunecore ? "primary" : "darkSecondary"}
                    className={isTunecore ? "border-layer-inverse text-white hover:bg-layer-inverse" : ""}
                  />
                  <Button
                    type="submit"
                    variant={isTunecore ? "secondary" : "primary"}
                    disabled={!canSubmit}
                    label={isUpdate ? t("songs:update-song-button") : t("songs:add-song-button")}
                    tooltip={!canSubmit && form.formState.isSubmitted && Object.keys(form.formState.errors).length > 0}
                    tooltipText={!canSubmit && form.formState.isSubmitted && Object.keys(form.formState.errors).length > 0 ? (t("common:see-validation-errors") ?? "See validation errors above") : undefined}
                  />

                </div>
              </form>
            </Form>
          )}

          {/* Writer Modals using Dialog components */}
          <Dialog open={showSelectWritersModal} onOpenChange={setShowSelectWritersModal}>
            <DialogContent className="max-w-4xl">
              <SelectExistingWritersModal
                availableWriters={writersQuery.data?.writers || []}
                selectedWriters={selectedWriters.map(writer => ({
                  writercode: writer.writercode,
                  writername: writer.writername,
                  caeipinumber: writer.caeipinumber ?? undefined,
                }))} // Convert WriterForSelection[] to SelectedWriter[]
                onWritersSelected={handleWritersSelected}
                onClose={() => setShowSelectWritersModal(false)}
              />
            </DialogContent>
          </Dialog>

          <Dialog
            open={showCreateWriterModal}
            onOpenChange={(open) => {
              setShowCreateWriterModal(open);
              if (!open) {
                setEditingWriterCode(null);
              }
            }}
          >
            <DialogContent className="max-w-2xl ">
              <CreateWriterModal
                onWriterCreated={handleWriterCreated}
                onWriterUpdated={handleWriterUpdated}
                onClose={() => {
                  setShowCreateWriterModal(false);
                  setEditingWriterCode(null);
                }}
                isUSPro={false}
                editingWriterCode={editingWriterCode}
                availableWriters={selectedWriters}
              />
            </DialogContent>
          </Dialog>

          {/* Create/Edit Recording Modal */}
          <Dialog open={showCreateRecordingModal} onOpenChange={setShowCreateRecordingModal}>
            <DialogContent className="max-w-3xl">
              <CreateRecordingForm
                onRecordingCreated={handleRecordingCreated}
                onClose={handleCloseRecordingModal}
                initialData={editingRecordingIndex !== null ? songData.recordings[editingRecordingIndex] : undefined}
                editingIndex={editingRecordingIndex}
              />
            </DialogContent>
          </Dialog>

          {/* Single Writer Warning Modal */}
          <SingleWriterWarningModal
            isOpen={isWarningModalOpen}
            onClose={handleWarningClose}
            onConfirm={handleWarningConfirm}
          />

          {/* Account Selection Modal for Deep Links */}
          <ChooseAccountModal
            open={showAccountModal}
            setAccountChoiceModal={setShowAccountModal}
            accounts={myAccounts.data?.artistaccounts}
            to={`${location.pathname}${location.search}`}
            title={t("songs:select-account-for-song", "Select the account you want to add this song to:")}
          />

          {id !== null && id !== undefined && song.isLoading && (
            <div className="flex justify-center">
              <div>Loading...</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
